import getData from "@/__main__/get-data.mjs";
import {
  EFTMapImages,
  EFTWeaponImages,
} from "@/data-models/eft-weapon-maps-images.mjs";
import { LeaderboardsTabsEnum } from "@/game-eft/constants/constants.mjs";
import fetchLeaderboards from "@/game-eft/fetches/leaderboards-page.mjs";
import { ItemsValidate } from "@/game-eft/models/graphql.mjs";
import { ItemsLocaleValidate } from "@/game-eft/models/static.mjs";
import * as API from "@/game-eft/utils/api.mjs";
import { devError } from "@/util/dev.mjs";

const options = {
  skipSafetyCheck: true, // This is called when EFT is running
};

export async function fetchStatic({
  isLoadAsync,
  isSearch = false,
}: {
  isLoadAsync: boolean;
  isSearch?: boolean;
}) {
  const promises = [getWeaponImgs, getMapImgs, getItemsFromBE] as Array<
    () => Promise<Partial<unknown> | unknown>
  >;
  if (!isSearch) {
    promises.push(getItemsTranslation, async () => {
      await fetchLeaderboards(
        [LeaderboardsTabsEnum.Weapons],
        new URLSearchParams(),
        undefined,
      );
    });
  }
  if (isLoadAsync) {
    try {
      await Promise.all(promises.map((i) => i()));
    } catch (e) {
      devError("Failed to fetch static data asynchronously", e);
    }
  } else {
    for (const promise of promises) {
      try {
        // The assets are extremely large payloads, during QA testing the EFT game has crashed,
        // we are unsure if its directly related to the Blitz client fetching the data and
        // comitting it to state but this should help a bit to reduce strain on the user
        await promise();
        await delayPromise();
      } catch (e) {
        devError("Failed to fetch static data non-async", e);
      }
    }
  }
}

const DELAY = 1e3;

const delayPromise = () =>
  new Promise((resolve) => {
    setTimeout(resolve, DELAY);
  });

const getWeaponImgs = () =>
  getData(
    API.getWeaponsImages(),
    EFTWeaponImages,
    ["eft", "imgs", "weapons"],
    options,
  );

const getMapImgs = () =>
  getData(API.getMapsImages(), EFTMapImages, ["eft", "imgs", "maps"], options);

const getItemsTranslation = () =>
  getData(
    API.getItemsLocale(),
    ItemsLocaleValidate,
    ["eft", "itemsLocale"],
    options,
  );

const getItemsFromBE = () =>
  getData(API.getItems(), ItemsValidate, ["eft", "items"], options);
